import { makeStyles, TextField, InputAdornment } from "@material-ui/core";
import React, { useEffect } from "react";
import { FieldError } from "react-hook-form";
import NumberFormat from "react-number-format";
import { PopoverTooltip } from "../../portico/tooltip/tooltip";
import { isFixedHelpTooltip } from "./form-helper-methods";
import HelpIcon from '@material-ui/icons/Help';
import { formatHelpText } from "../../../utils/common-utils";
import { Parser } from 'html-to-react';

export default function FormattedInputs({ initialData = '', field, fieldName, onChange, formMethods, fieldValue }) {
    const { register, setValue, errors, unregister } = formMethods;
    const [value, setData] = React.useState(fieldValue);
    const [errorText, setErrorText] = React.useState<any | null>(null);
    const [shrink, setShrink] = React.useState(value ? true : false)
    const validationsRules = field.validationRule?.validationData || {};
    const mandatoryRule = validationsRules.isMandatory || {};
    const isRequired = mandatoryRule.active;
    const reqValidationMessage = mandatoryRule.message;

    useEffect(() => {
        return () => unregister(fieldName);
    }, [])

    const outline = makeStyles({
        root: {
            "& .MuiOutlinedInput-input": { padding: "13.5px", marginLeft: "-4px" },
            "& .MuiFormHelperText-root.Mui-error": { color: "var(--light-venetian-red);" },
            "& .MuiFormLabel-root.Mui-error": { color: "var(--fire-brick-red);" },
            "& .MuiOutlinedInput-root.Mui-error.MuiOutlinedInput-notchedOutline": { color: "var(--light-venetian-red);", borderColor: "var(--fire-brick-red);", },
            "& .MuiFormHelperText-contained": { marginLeft: "1px" },
            "& .MuiFormHelperText-root": { fontSize: "11px", marginTop:'8px' },
            "& .MuiFormLabel-asterisk.MuiInputLabel-asterisk": { color: "var(--light-venetian-red);" },
            "& .MuiInputLabel-root": { "&.Mui-focused": { color: "var(--input-focus-border-color)" }, "&.Mui-error": { color: "var(--light-venetian-red);" } }        },
            endAdornmentWrapper: {
            marginRight: '8px',
            marginLeft: '0px',
            marginTop: '4px',
            zIndex: 1,
            cursor: 'auto',

            '& svg': {
                color: 'var(--teal-blue)'
            }
        }

    });
    const c = outline();

    const numberLimitRule = validationsRules.numberLimit || {};
    const minNumberValue = numberLimitRule.minValue;
    const maxNumberValue = numberLimitRule.maxValue;
    const numberLimitApplied = numberLimitRule.active;
    const numberLimitValidationMessage = numberLimitRule.message;

    const noNegativeRule = validationsRules.noNegative || {};
    const noNegative = noNegativeRule.active;
    const noNegativeValidationMessage = noNegativeRule.message;

    const onlyIntegerRule = validationsRules.onlyInteger || {};
    const onlyInteger = onlyIntegerRule.active;
    const onlyIntegerValidationMessage = onlyIntegerRule.message;
    const onlyDecimalRule = validationsRules.onlyDecimal || {};
    const onlyDecimal = onlyDecimalRule.active;
    const onlyDecimalValidationMessage = onlyDecimalRule.message;
    const decimalScale = onlyDecimal ? 2 : 0
    const getErrorText = (updatedValue) => {
        const numberValue = Number(updatedValue);

        if (updatedValue === '' && isRequired) {
            return reqValidationMessage
        }

        if (isNaN(numberValue) || ( value && (/ |^\+|e|^-0+$|^-0+\.$|^-0+\.0+$/.test(value)))) {  // if not a standard number or has + sign or in scientific notation
            return "Field must be a valid number."
        }
        if (noNegative && numberValue < 0) {
            return noNegativeValidationMessage
        }
        if (onlyInteger && (!Number.isInteger(numberValue) || numberValue.toString() !== value)) {
            return onlyIntegerValidationMessage
        }
        if (numberLimitApplied && ((minNumberValue && (minNumberValue > numberValue)) || (maxNumberValue && (maxNumberValue < numberValue)))) {
            return numberLimitValidationMessage
        }
        if (onlyDecimal) {
            const text = numberValue.toString()   // using value controls unlimited 0s after decimal point.
            const index = text.indexOf(".")
            const decimalPlaces = index === -1 ? 0 : (text.length - index - 1);
            if (decimalPlaces > 2 || (index >= 0 && (decimalPlaces === 0))) {
                return onlyDecimalValidationMessage
            }
        }
        return ''
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        let valueEntered = !isNaN(parseFloat(event.target.value)) && parseFloat(event.target.value) >= 0 ? event.target.value : "";

       // let valueEntered = event.target.value;
        
        if (valueEntered) {
            setData(valueEntered);
            setValue(event.target.name, valueEntered, { shouldValidate: true });
            onChange();
            const errorMsg = getErrorText(valueEntered)
            setErrorText(errorMsg ? {message: errorMsg}: '');
            setShrink(true);
        } else {
            setData(valueEntered);
            setValue(event.target.name, valueEntered, { shouldValidate: true });
            onChange();
            setShrink(false);
        }
    };
    const handleFocus = (type: boolean) => {
        if (value) {
            setShrink(true);
        } else {
            setShrink(type);
        }
    }

    return (
        <TextField
            {...register(fieldName, {
                required: {
                  value: isRequired,
                  message: reqValidationMessage
                },
              })}
            name={fieldName}
            label={field.name}
            value={value}
            className={c.root}
            required={isRequired}
            onChange={handleChange}
            id="formatted-numberformat-input"
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        {field.description && 
                        <div className={c.endAdornmentWrapper}>
                            <PopoverTooltip messageDescription={Parser().parse(formatHelpText(field.description))}>
                                <HelpIcon />
                                </PopoverTooltip>
                                </div>}
                    </InputAdornment>
                ),
                inputComponent: NumberFormatCustom as any,
                title: ' ',
                inputProps: { title: ' ', decimalScale: decimalScale }
            }}    
            InputLabelProps={{
                shrink: shrink,
                required: false
            }}                                                                                                                                                                                      
            variant="outlined"
            style={{ width: '100%' }}
            error={errors[fieldName] || errors[field.identifier]|| errorText}
            helperText={errors[fieldName]?.message || errorText?.message}
            onFocus={() => handleFocus(true)}
            onBlur={() => handleFocus(false)}
            fullWidth
        >
        </TextField>
    );
}


interface CustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
    error?: FieldError;
    errorMessage?: string;
    decimalScale?: number;
    inputRef: any;
}

const NumberFormatCustom = React.forwardRef<NumberFormat<any>, CustomProps>(
    function NumberFormatCustom(props, ref) {
        const { onChange, inputRef, decimalScale, ...other } = props;

        return (
            <NumberFormat
                {...other}
                title=' '
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value
                        },
                    });
                }}
                thousandSeparator
                isNumericString
                prefix="$"
                decimalScale={decimalScale}
            />
        );
    },
);